import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Icon,
  Modal,
  Form,
  Message,
  Label,
  Input,
  TextArea,
  Pagination,
  Popup,
  Loader,
  Tab,
  Segment,
  Grid,
  Divider
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import MonacoCodeEditor from '../../components/MonacoCodeEditor';
import './index.css';

// ==================== 默认代码模板 ====================
const DEFAULT_CODE = `/**
 * 动态API处理函数
 * 
 * 内置API:
 *   - llm.chat({ model, messages, temperature?, max_tokens? })
 *   - templates.render(name, vars) - 渲染模板
 *   - templates.get(name) / list() / has(name)
 *   - utils.parseJSON(str), utils.stringify(obj), utils.sleep(ms)
 *   - console.log/info/warn/error/debug(...)
 * 
 * @param {Object} input - 请求对象
 * @returns {Object} OpenAI格式的响应对象
 */
function process(input) {
    console.log('收到请求:', utils.stringify(input));
    
    // 示例: 使用模板构建prompt
    var systemPrompt = templates.has('system') 
        ? templates.render('system', { user: input.user || 'User' }).result
        : '你是一个有用的助手。';
    
    // 调用LLM
    var result = llm.chat({
        model: input.model || "gpt-4o-mini",
        messages: [
            { role: "system", content: systemPrompt },
            ...(input.messages || [])
        ],
        temperature: 0.7
    });

    // 检查结果
    if (!result || !result.success) {
        var errMsg = '未知错误';
        if (result && result.error) {
            errMsg = result.error.message || result.error.code || '调用失败';
        }
        console.error('LLM调用失败:', errMsg);
        return {
            error: { type: "api_error", message: errMsg }
        };
    }

    console.log('调用成功，tokens:', result.usage ? result.usage.total_tokens : 0);
    return result.data;
}`;

// ==================== 默认模板 ====================
const DEFAULT_TEMPLATES = {
  system: {
    content: '你是一个专业的AI助手，正在帮助 {{.user}} 解决问题。请用中文回复。',
    description: '系统提示词模板'
  }
};

// ==================== 模板编辑器组件 ====================
const TemplatesEditor = ({ templates, onChange }) => {
  const [templateList, setTemplateList] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', content: '', description: '' });

  // 解析模板 JSON
  useEffect(() => {
    try {
      const parsed = templates ? JSON.parse(templates) : {};
      const list = Object.entries(parsed).map(([name, data]) => ({
        name,
        content: data.content || '',
        description: data.description || ''
      }));
      setTemplateList(list);
    } catch {
      setTemplateList([]);
    }
  }, [templates]);

  // 保存模板列表
  const saveTemplates = (list) => {
    const obj = {};
    list.forEach(t => {
      obj[t.name] = { content: t.content, description: t.description };
    });
    onChange(JSON.stringify(obj));
  };

  // 添加模板
  const handleAdd = () => {
    if (!newTemplate.name) {
      showError('请输入模板名称');
      return;
    }
    if (templateList.some(t => t.name === newTemplate.name)) {
      showError('模板名称已存在');
      return;
    }
    const newList = [...templateList, { ...newTemplate }];
    setTemplateList(newList);
    saveTemplates(newList);
    setShowAddModal(false);
    setNewTemplate({ name: '', content: '', description: '' });
  };

  // 更新模板
  const handleUpdate = () => {
    const newList = templateList.map(t =>
      t.name === editingTemplate.name ? editingTemplate : t
    );
    setTemplateList(newList);
    saveTemplates(newList);
    setEditingTemplate(null);
  };

  // 删除模板
  const handleDelete = (name) => {
    if (!window.confirm(`确定删除模板 "${name}" 吗？`)) return;
    const newList = templateList.filter(t => t.name !== name);
    setTemplateList(newList);
    saveTemplates(newList);
  };

  return (
    <div className="templates-editor">
      <div className="templates-header">
        <span className="templates-count">共 {templateList.length} 个模板</span>
        <Button size="mini" primary onClick={() => setShowAddModal(true)}>
          <Icon name="plus" /> 添加模板
        </Button>
      </div>

      {templateList.length === 0 ? (
        <Message info size="small">
          <Message.Header>暂无模板</Message.Header>
          <p>模板可以在JS代码中通过 <code>templates.render(name, vars)</code> 调用</p>
        </Message>
      ) : (
        <div className="templates-list">
          {templateList.map(t => (
            <div key={t.name} className="template-item">
              <div className="template-item-header">
                <span className="template-name">{t.name}</span>
                <div className="template-actions">
                  <Button size="mini" basic onClick={() => setEditingTemplate({ ...t })}>
                    <Icon name="edit" />
                  </Button>
                  <Button size="mini" basic color="red" onClick={() => handleDelete(t.name)}>
                    <Icon name="trash" />
                  </Button>
                </div>
              </div>
              {t.description && <div className="template-description">{t.description}</div>}
              <pre className="template-content">{t.content}</pre>
            </div>
          ))}
        </div>
      )}

      {/* 添加模板弹窗 */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} size="large">
        <Modal.Header>添加模板</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>模板名称</label>
                <Input
                  placeholder="如: system, greeting"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                />
              </Form.Field>
              <Form.Field>
                <label>描述</label>
                <Input
                  placeholder="模板用途说明"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field required>
              <label>
                模板内容 
                <span style={{ color: '#888', fontWeight: 'normal', marginLeft: 8 }}>
                  支持 Go template 语法: {'{{.variable}}'}, {'{{if .cond}}...{{end}}'}
                </span>
              </label>
              <MonacoCodeEditor
                value={newTemplate.content}
                onChange={(val) => setNewTemplate({ ...newTemplate, content: val })}
                language="gotemplate"
                height="300px"
                theme="vs-dark"
                placeholder="你是一个专业的助手，正在帮助 {{.user}} 解决问题。"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowAddModal(false)}>取消</Button>
          <Button primary onClick={handleAdd}>添加</Button>
        </Modal.Actions>
      </Modal>

      {/* 编辑模板弹窗 */}
      <Modal open={!!editingTemplate} onClose={() => setEditingTemplate(null)} size="large">
        <Modal.Header>编辑模板 - {editingTemplate?.name}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>描述</label>
              <Input
                placeholder="模板用途说明"
                value={editingTemplate?.description || ''}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
              />
            </Form.Field>
            <Form.Field required>
              <label>
                模板内容
                <span style={{ color: '#888', fontWeight: 'normal', marginLeft: 8 }}>
                  支持 Go template 语法
                </span>
              </label>
              <MonacoCodeEditor
                value={editingTemplate?.content || ''}
                onChange={(val) => setEditingTemplate({ ...editingTemplate, content: val })}
                language="gotemplate"
                height="350px"
                theme="vs-dark"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setEditingTemplate(null)}>取消</Button>
          <Button primary onClick={handleUpdate}>保存</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

// ==================== 主组件 ====================
const DynamicAPI = () => {
  // 列表状态
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApi, setSelectedApi] = useState(null);
  const [activeTab, setActiveTab] = useState('code');

  // 版本状态
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // 测试状态
  const [testRequest, setTestRequest] = useState(JSON.stringify({
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: "Hello!" }]
  }, null, 2));
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [responseTab, setResponseTab] = useState('response');

  // 日志状态
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  // 模态框状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', display_name: '', description: '', category: '', timeout: 30, rate_limit: 10
  });
  const [editForm, setEditForm] = useState({
    display_name: '', description: '', category: '', timeout: 30, rate_limit: 10
  });
  const [versionForm, setVersionForm] = useState({
    version: '', code: DEFAULT_CODE, templates: JSON.stringify(DEFAULT_TEMPLATES), change_log: ''
  });
  const [versionEditorTab, setVersionEditorTab] = useState('code'); // 'code' | 'templates'

  // 语法检查状态
  const [syntaxErrors, setSyntaxErrors] = useState([]);

  // 计算下一个版本号
  const getNextVersion = (versions) => {
    if (!versions || versions.length === 0) return 'v1.0.0';
    const latest = versions[0];
    const match = latest.version.match(/v?(\d+)\.(\d+)\.(\d+)/);
    if (match) {
      const [, major, minor, patch] = match;
      return `v${major}.${minor}.${parseInt(patch) + 1}`;
    }
    return `v1.0.${versions.length}`;
  };

  // 打开新建版本模态框
  const openNewVersionModal = () => {
    const latestVersion = versions[0];
    setVersionForm({
      version: getNextVersion(versions),
      code: latestVersion?.code || DEFAULT_CODE,
      templates: latestVersion?.templates || JSON.stringify(DEFAULT_TEMPLATES),
      change_log: ''
    });
    setVersionEditorTab('code');
    setSyntaxErrors([]);
    setShowNewVersionModal(true);
  };

  // 打开编辑配置模态框
  const openEditModal = () => {
    setEditForm({
      display_name: selectedApi.display_name || '',
      description: selectedApi.description || '',
      category: selectedApi.category || '',
      timeout: selectedApi.timeout || 30,
      rate_limit: selectedApi.rate_limit || 10
    });
    setShowEditModal(true);
  };

  // 加载API列表
  const loadApis = useCallback(async () => {
    try {
      const res = await API.get('/api/admin/dynamic-apis', { params: { page: 1, page_size: 100 } });
      if (res.data.success) {
        setApis(res.data.data.list || []);
      }
    } catch (err) {
      showError('加载API列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载版本列表
  const loadVersions = useCallback(async (apiId) => {
    setVersionsLoading(true);
    try {
      const res = await API.get(`/api/admin/dynamic-apis/${apiId}/versions`);
      if (res.data.success) {
        setVersions(res.data.data || []);
      }
    } catch (err) {
      showError('加载版本失败');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  // 加载日志
  const loadLogs = useCallback(async (apiId, page = 1) => {
    setLogsLoading(true);
    try {
      const res = await API.get(`/api/admin/dynamic-apis/${apiId}/logs`, {
        params: { page, page_size: 10 }
      });
      if (res.data.success) {
        setLogs(res.data.data.list || []);
        setLogsTotal(res.data.data.total || 0);
        setLogsPage(page);
      }
    } catch (err) {
      showError('加载日志失败');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApis();
  }, [loadApis]);

  // 选择API
  const handleSelectApi = async (api) => {
    setSelectedApi(api);
    setActiveTab('code');
    setTestResult(null);
    await loadVersions(api.id);
  };

  // 创建API
  const handleCreate = async () => {
    if (!createForm.name) {
      showError('请输入API名称');
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(createForm.name)) {
      showError('API名称必须以字母开头，只能包含字母、数字、下划线和横线');
      return;
    }
    try {
      const res = await API.post('/api/admin/dynamic-apis', createForm);
      if (res.data.success) {
        showSuccess('创建成功');
        setShowCreateModal(false);
        setCreateForm({ name: '', display_name: '', description: '', category: '', timeout: 30, rate_limit: 10 });
        loadApis();
      }
    } catch (err) {
      showError('创建失败: ' + (err.response?.data?.message || err.message));
    }
  };

  // 删除API
  const handleDelete = async () => {
    if (!window.confirm(`确定要删除 ${selectedApi.name} 吗？此操作不可恢复。`)) return;
    try {
      const res = await API.delete(`/api/admin/dynamic-apis/${selectedApi.id}`);
      if (res.data.success) {
        showSuccess('删除成功');
        setSelectedApi(null);
        loadApis();
      }
    } catch (err) {
      showError('删除失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async () => {
    const newStatus = selectedApi.status === 1 ? 0 : 1;
    try {
      const res = await API.put(`/api/admin/dynamic-apis/${selectedApi.id}`, { status: newStatus });
      if (res.data.success) {
        showSuccess(newStatus === 1 ? '已启用' : '已禁用');
        setSelectedApi({ ...selectedApi, status: newStatus });
        loadApis();
      }
    } catch (err) {
      showError('操作失败');
    }
  };

  // 创建版本
  const handleCreateVersion = async () => {
    if (!versionForm.version || !versionForm.code) {
      showError('请填写版本号和代码');
      return;
    }
    if (!/^v?\d+\.\d+\.\d+$/.test(versionForm.version)) {
      showError('版本号格式应为 v1.0.0 或 1.0.0');
      return;
    }
    if (syntaxErrors.length > 0) {
      showError('代码存在语法错误，请先修复');
      return;
    }
    try {
      const res = await API.post(`/api/admin/dynamic-apis/${selectedApi.id}/versions`, versionForm);
      if (res.data.success) {
        showSuccess('版本创建成功');
        setShowNewVersionModal(false);
        loadVersions(selectedApi.id);
      }
    } catch (err) {
      showError('创建失败: ' + (err.response?.data?.message || err.message));
    }
  };

  // 保存配置编辑
  const handleSaveConfig = async () => {
    try {
      const res = await API.put(`/api/admin/dynamic-apis/${selectedApi.id}`, editForm);
      if (res.data.success) {
        showSuccess('配置已保存');
        setShowEditModal(false);
        setSelectedApi({ ...selectedApi, ...editForm });
        loadApis();
      }
    } catch (err) {
      showError('保存失败: ' + (err.response?.data?.message || err.message));
    }
  };

  // 激活版本
  const handleActivateVersion = async (versionId) => {
    try {
      const res = await API.put(`/api/admin/dynamic-apis/${selectedApi.id}/versions/${versionId}/activate`);
      if (res.data.success) {
        showSuccess('版本已激活');
        loadVersions(selectedApi.id);
      }
    } catch (err) {
      showError('激活失败');
    }
  };

  // 删除版本
  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('确定要删除此版本吗？')) return;
    try {
      const res = await API.delete(`/api/admin/dynamic-apis/${selectedApi.id}/versions/${versionId}`);
      if (res.data.success) {
        showSuccess('已删除');
        loadVersions(selectedApi.id);
      }
    } catch (err) {
      showError('删除失败: ' + (err.response?.data?.message || err.message));
    }
  };

  // 执行测试
  const handleTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const requestObj = JSON.parse(testRequest);
      const currentVersion = versions.find(v => v.is_active);
      const res = await API.post(`/api/admin/dynamic-apis/${selectedApi.id}/test`, {
        version_id: currentVersion?.id || '',
        request: requestObj
      });
      if (res.data.success) {
        setTestResult(res.data.data);
        setResponseTab('response');
      } else {
        showError(res.data.message || '测试失败');
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        showError('请求JSON格式错误');
      } else {
        const errorMessage = err.response?.data?.message || err.message;
        setTestResult({
          result: null,
          error: errorMessage,
          execution_ok: false,
          duration_ms: 0,
          total_tokens: 0,
          relay_history: [],
          logs: []
        });
        setResponseTab('response');
      }
    } finally {
      setTestLoading(false);
    }
  };

  // 语法检查回调
  const handleCodeValidation = ({ errors }) => {
    setSyntaxErrors(errors);
  };

  // Tab切换时加载数据
  useEffect(() => {
    if (selectedApi && activeTab === 'logs') {
      loadLogs(selectedApi.id, 1);
    }
  }, [selectedApi, activeTab, loadLogs]);

  // 获取当前活跃版本
  const activeVersion = versions.find(v => v.is_active);

  // 解析活跃版本的模板
  const activeTemplates = useMemo(() => {
    if (!activeVersion?.templates) return {};
    try {
      return JSON.parse(activeVersion.templates);
    } catch {
      return {};
    }
  }, [activeVersion]);

  return (
    <div className="dynamic-api-page">
      {/* 侧边栏 */}
      <div className="dynamic-api-sidebar">
        <div className="sidebar-header">
          <h3>动态API</h3>
          <Button primary size="small" onClick={() => setShowCreateModal(true)}>
            <Icon name="plus" /> 新建
          </Button>
        </div>
        <div className="sidebar-list">
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><Loader active inline /></div>
          ) : apis.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>暂无API</div>
          ) : (
            apis.map(api => (
              <div
                key={api.id}
                className={`api-item ${selectedApi?.id === api.id ? 'active' : ''}`}
                onClick={() => handleSelectApi(api)}
              >
                <div className="api-item-name">
                  <span className={`api-item-status ${api.status === 1 ? 'enabled' : 'disabled'}`} />
                  {api.display_name || api.name}
                </div>
                <div className="api-item-meta">
                  <code>/v1/api/{api.name}</code>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 主内容区 */}
      <div className="dynamic-api-main">
        {!selectedApi ? (
          <div className="empty-state">
            <Icon name="code" className="empty-state-icon" />
            <h3>选择或创建一个动态API</h3>
            <p>动态API允许你使用JavaScript编写自定义的API处理逻辑，<br />支持多轮LLM调用、模板渲染、前后处理等高级功能。</p>
          </div>
        ) : (
          <div className="api-detail">
            {/* 头部信息 */}
            <div className="api-detail-header">
              <div className="api-detail-title">
                <h2>{selectedApi.display_name || selectedApi.name}</h2>
                <span className={`status-badge ${selectedApi.status === 1 ? 'enabled' : 'disabled'}`}>
                  {selectedApi.status === 1 ? '运行中' : '已禁用'}
                </span>
              </div>
              <div className="api-detail-meta">
                <span><Icon name="linkify" /> /v1/api/{selectedApi.name}</span>
                <span><Icon name="clock" /> 超时 {selectedApi.timeout}s</span>
                <span><Icon name="repeat" /> 最多 {selectedApi.rate_limit} 次调用</span>
                {activeVersion && <span><Icon name="tag" /> {activeVersion.version}</span>}
              </div>
              <div className="api-detail-actions">
                <Button size="small" onClick={handleToggleStatus}>
                  <Icon name={selectedApi.status === 1 ? 'pause' : 'play'} />
                  {selectedApi.status === 1 ? '禁用' : '启用'}
                </Button>
                <Button size="small" color="red" onClick={handleDelete}>
                  <Icon name="trash" /> 删除
                </Button>
              </div>
            </div>

            {/* Tab导航 */}
            <div className="api-tabs">
              {['code', 'templates', 'versions', 'test', 'logs', 'config'].map(tab => (
                <div
                  key={tab}
                  className={`api-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  <Icon name={{
                    code: 'code', templates: 'file alternate', versions: 'history', test: 'play', logs: 'list', config: 'cog'
                  }[tab]} />
                  {{ code: '代码', templates: '模板', versions: '版本', test: '测试', logs: '日志', config: '配置' }[tab]}
                </div>
              ))}
            </div>

            {/* Tab内容 */}
            <div className="api-tab-content">
              {/* 代码Tab */}
              {activeTab === 'code' && (
                <div>
                  {versionsLoading ? (
                    <Loader active inline="centered" />
                  ) : activeVersion ? (
                    <>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#666', fontSize: 13 }}>
                          当前版本: <Label size="small" color="green">{activeVersion.version}</Label>
                        </span>
                        <Button size="small" primary onClick={openNewVersionModal}>
                          <Icon name="plus" /> 新建版本
                        </Button>
                      </div>
                      <MonacoCodeEditor
                        value={activeVersion.code}
                        readOnly
                        height={500}
                        language="javascript"
                      />
                    </>
                  ) : (
                    <Message info>
                      <Message.Header>暂无版本</Message.Header>
                      <p>请先创建一个版本来编写API代码</p>
                      <Button primary onClick={openNewVersionModal}>
                        <Icon name="plus" /> 创建第一个版本
                      </Button>
                    </Message>
                  )}
                </div>
              )}

              {/* 模板Tab */}
              {activeTab === 'templates' && (
                <div>
                  {versionsLoading ? (
                    <Loader active inline="centered" />
                  ) : activeVersion ? (
                    <>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#666', fontSize: 13 }}>
                          版本 <Label size="small" color="green">{activeVersion.version}</Label> 的模板
                        </span>
                        <Button size="small" primary onClick={openNewVersionModal}>
                          <Icon name="plus" /> 新建版本编辑模板
                        </Button>
                      </div>
                      {Object.keys(activeTemplates).length === 0 ? (
                        <Message info>
                          <Message.Header>暂无模板</Message.Header>
                          <p>此版本未配置模板。创建新版本时可以添加模板。</p>
                        </Message>
                      ) : (
                        <div className="templates-preview">
                          {Object.entries(activeTemplates).map(([name, data]) => (
                            <div key={name} className="template-preview-item">
                              <div className="template-preview-header">
                                <code>{name}</code>
                                {data.description && <span className="template-desc">{data.description}</span>}
                              </div>
                              <pre className="template-preview-content">{data.content}</pre>
                            </div>
                          ))}
                        </div>
                      )}
                      <Divider />
                      <Message size="small">
                        <Message.Header>模板语法</Message.Header>
                        <p>使用 Go template 语法，例如：<code>{'{{.variable}}'}</code>，<code>{'{{if .condition}}...{{end}}'}</code></p>
                        <p>在JS代码中调用：<code>templates.render("name", {'{ variable: "value" }'})</code></p>
                      </Message>
                    </>
                  ) : (
                    <Message info>
                      <Message.Header>暂无版本</Message.Header>
                      <p>请先创建一个版本来配置模板</p>
                      <Button primary onClick={openNewVersionModal}>
                        <Icon name="plus" /> 创建第一个版本
                      </Button>
                    </Message>
                  )}
                </div>
              )}

              {/* 版本Tab */}
              {activeTab === 'versions' && (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>共 {versions.length} 个版本（按创建时间降序）</span>
                    <Button primary size="small" onClick={openNewVersionModal}>
                      <Icon name="plus" /> 新建版本
                    </Button>
                  </div>
                  {versionsLoading ? (
                    <Loader active inline="centered" />
                  ) : versions.length === 0 ? (
                    <Message info>暂无版本，请创建第一个版本</Message>
                  ) : (
                    <div className="version-list">
                      {versions.map(v => (
                        <div key={v.id} className={`version-card ${v.is_active ? 'active' : ''}`}>
                          <div className="version-card-header">
                            <div className="version-card-title">
                              <h4>{v.version}</h4>
                              {v.is_active && <span className="version-badge">当前版本</span>}
                            </div>
                            <div className="version-card-actions">
                              {!v.is_active && (
                                <>
                                  <Button size="mini" positive onClick={() => handleActivateVersion(v.id)}>
                                    激活
                                  </Button>
                                  <Button size="mini" negative onClick={() => handleDeleteVersion(v.id)}>
                                    删除
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="version-card-meta">
                            创建于 {timestamp2string(new Date(v.created_at).getTime() / 1000)}
                            {v.change_log && ` · ${v.change_log}`}
                          </div>
                          <div className="version-card-stats">
                            <span>调用次数: {v.usage_count || 0}</span>
                            <span>平均耗时: {v.avg_duration || 0}ms</span>
                            <span>模板数: {(() => {
                              try { return Object.keys(JSON.parse(v.templates || '{}')).length; }
                              catch { return 0; }
                            })()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 测试Tab */}
              {activeTab === 'test' && (
                <div className="test-panel">
                  <div className="test-panel-section request-section">
                    <div className="test-panel-header">
                      <h4>请求 (JSON)</h4>
                      <Button primary size="small" onClick={handleTest} loading={testLoading}>
                        <Icon name="play" /> 执行
                      </Button>
                    </div>
                    <div className="test-panel-body">
                      <MonacoCodeEditor
                        value={testRequest}
                        onChange={setTestRequest}
                        height={450}
                        language="json"
                      />
                    </div>
                  </div>
                  <div className="test-panel-section response-section">
                    <div className="test-panel-header">
                      <h4>响应</h4>
                      {testResult && (
                        <span className={`execution-status ${testResult.execution_ok ? 'success' : 'error'}`}>
                          <Icon name={testResult.execution_ok ? 'check circle' : 'times circle'} />
                          {testResult.execution_ok ? '成功' : '失败'} · {testResult.duration_ms}ms · {testResult.total_tokens} tokens
                        </span>
                      )}
                    </div>
                    {testResult ? (
                      <>
                        <div className="response-tabs">
                          {['response', 'logs', 'relay'].map(tab => (
                            <div
                              key={tab}
                              className={`response-tab ${responseTab === tab ? 'active' : ''}`}
                              onClick={() => setResponseTab(tab)}
                            >
                              {{ response: '📤 结果', logs: `📋 日志 (${testResult.logs?.length || 0})`, relay: `🔗 调用 (${testResult.relay_history?.length || 0})` }[tab]}
                            </div>
                          ))}
                        </div>
                        <div className="response-content">
                          {responseTab === 'response' && (
                            <pre className="response-json">
                              {JSON.stringify(testResult.result || testResult.error || {}, null, 2)}
                            </pre>
                          )}
                          {responseTab === 'logs' && (
                            <div className="console-logs">
                              {testResult.logs?.length > 0 ? (
                                testResult.logs.map((log, i) => (
                                  <div key={i} className={`log-item ${log.level}`}>
                                    <span className="log-time">{new Date(log.time).toLocaleTimeString()}</span>
                                    <span className="log-level">[{log.level.toUpperCase()}]</span>
                                    <span className="log-message">{log.message}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="empty-logs">无日志输出（使用 console.log() 输出日志）</div>
                              )}
                            </div>
                          )}
                          {responseTab === 'relay' && (
                            <div className="relay-calls">
                              {testResult.relay_history?.length > 0 ? (
                                testResult.relay_history.map((call, i) => (
                                  <div key={i} className={`relay-call-item ${call.success ? 'success' : 'error'}`}>
                                    <span className="relay-call-index">#{i + 1}</span>
                                    <span className="relay-call-model">
                                      <span className="model-name">{call.request_model}</span>
                                    </span>
                                    <span className="relay-call-status">{call.success ? '✅' : '❌'}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="empty-logs">无 Relay 调用（在代码中使用 llm.chat() 调用模型）</div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="empty-response">
                        <Icon name="terminal" size="huge" />
                        <p>点击 "执行" 按钮测试 API</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 日志Tab */}
              {activeTab === 'logs' && (
                <div>
                  {logsLoading ? (
                    <Loader active inline="centered" />
                  ) : logs.length === 0 ? (
                    <Message info>暂无调用日志</Message>
                  ) : (
                    <>
                      <table className="logs-table">
                        <thead>
                          <tr>
                            <th>时间</th>
                            <th>状态</th>
                            <th>耗时</th>
                            <th>调用次数</th>
                            <th>Tokens</th>
                            <th>错误</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map(log => (
                            <tr key={log.id}>
                              <td>{timestamp2string(new Date(log.created_at).getTime() / 1000)}</td>
                              <td>
                                <span className={`log-status-badge ${['success', 'failed', 'timeout'][log.status]}`}>
                                  {['成功', '失败', '超时'][log.status]}
                                </span>
                              </td>
                              <td>{log.duration_ms}ms</td>
                              <td>{log.relay_call_count}</td>
                              <td>{log.total_tokens}</td>
                              <td>
                                {log.error_message && (
                                  <Popup content={log.error_message} trigger={
                                    <span style={{ color: '#ff4d4f', cursor: 'pointer' }}>查看</span>
                                  } />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {logsTotal > 10 && (
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                          <Pagination
                            activePage={logsPage}
                            totalPages={Math.ceil(logsTotal / 10)}
                            onPageChange={(e, { activePage }) => loadLogs(selectedApi.id, activePage)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 配置Tab */}
              {activeTab === 'config' && (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button primary size="small" onClick={openEditModal}>
                      <Icon name="edit" /> 编辑配置
                    </Button>
                  </div>
                  <div className="config-section">
                    <h4>基本信息</h4>
                    <div className="config-row">
                      <span className="config-label">API名称</span>
                      <span className="config-value"><code>{selectedApi.name}</code></span>
                      <span className="config-hint">URL路由名称，创建后不可修改</span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">显示名称</span>
                      <span className="config-value">{selectedApi.display_name || '-'}</span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">描述</span>
                      <span className="config-value">{selectedApi.description || '-'}</span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">分类</span>
                      <span className="config-value">{selectedApi.category || '-'}</span>
                    </div>
                  </div>
                  <div className="config-section">
                    <h4>运行配置</h4>
                    <div className="config-row">
                      <span className="config-label">执行超时</span>
                      <span className="config-value">{selectedApi.timeout} 秒</span>
                      <span className="config-hint">JS代码执行的最大时间</span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">LLM调用限制</span>
                      <span className="config-value">最多 {selectedApi.rate_limit} 次</span>
                      <span className="config-hint">单次执行中 llm.chat() 的最大调用次数，防止无限循环</span>
                    </div>
                  </div>
                  <div className="config-section">
                    <h4>调用方式</h4>
                    <div className="config-row">
                      <span className="config-label">端点URL</span>
                      <span className="config-value">
                        <code>POST {window.location.origin}/v1/api/{selectedApi.name}</code>
                      </span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">认证方式</span>
                      <span className="config-value">Bearer Token（与标准API相同）</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 创建API模态框 */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} size="small">
        <Modal.Header>创建动态API</Modal.Header>
        <Modal.Content>
          <Form className="create-form">
            <Form.Field required>
              <label>API名称 (URL路径，以字母开头，仅支持字母数字下划线横线)</label>
              <Input
                placeholder="my_custom_api"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>显示名称</label>
              <Input
                placeholder="我的自定义API"
                value={createForm.display_name}
                onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>描述</label>
              <TextArea
                placeholder="API功能描述"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </Form.Field>
            <div className="field-row">
              <Form.Field>
                <label>分类</label>
                <Input
                  placeholder="通用"
                  value={createForm.category}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                />
              </Form.Field>
              <Form.Field>
                <label>超时时间(秒)</label>
                <Input
                  type="number"
                  value={createForm.timeout}
                  onChange={(e) => setCreateForm({ ...createForm, timeout: parseInt(e.target.value) || 30 })}
                />
              </Form.Field>
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowCreateModal(false)}>取消</Button>
          <Button primary onClick={handleCreate}>创建</Button>
        </Modal.Actions>
      </Modal>

      {/* 新建版本模态框 */}
      <Modal open={showNewVersionModal} onClose={() => setShowNewVersionModal(false)} size="fullscreen" className="version-modal">
        <Modal.Header>
          <Icon name="code branch" /> 新建版本 - {selectedApi?.name}
        </Modal.Header>
        <Modal.Content scrolling>
          <Form className="version-form">
            <div className="version-form-header">
              <Form.Field required width={4}>
                <label>版本号</label>
                <Input
                  placeholder="v1.0.0"
                  value={versionForm.version}
                  onChange={(e) => setVersionForm({ ...versionForm, version: e.target.value })}
                />
              </Form.Field>
              <Form.Field width={8}>
                <label>更新说明</label>
                <Input
                  placeholder="初始版本 / 修复bug / 新增功能"
                  value={versionForm.change_log}
                  onChange={(e) => setVersionForm({ ...versionForm, change_log: e.target.value })}
                />
              </Form.Field>
              <div className="version-editor-tabs">
                <Button.Group>
                  <Button
                    active={versionEditorTab === 'code'}
                    onClick={() => setVersionEditorTab('code')}
                  >
                    <Icon name="code" /> 代码
                    {syntaxErrors.length > 0 && <Label color="red" size="mini" circular>{syntaxErrors.length}</Label>}
                  </Button>
                  <Button
                    active={versionEditorTab === 'templates'}
                    onClick={() => setVersionEditorTab('templates')}
                  >
                    <Icon name="file alternate" /> 模板
                  </Button>
                </Button.Group>
              </div>
            </div>

            {versionEditorTab === 'code' && (
              <div className="version-code-editor">
                <MonacoCodeEditor
                  value={versionForm.code}
                  onChange={(code) => setVersionForm({ ...versionForm, code })}
                  height={550}
                  language="javascript"
                  onValidate={handleCodeValidation}
                />
                {syntaxErrors.length > 0 && (
                  <Message error size="small" style={{ marginTop: 10 }}>
                    <Message.Header>代码存在 {syntaxErrors.length} 个语法错误</Message.Header>
                    <Message.List>
                      {syntaxErrors.slice(0, 5).map((err, i) => (
                        <Message.Item key={i}>
                          行 {err.startLineNumber}: {err.message}
                        </Message.Item>
                      ))}
                    </Message.List>
                  </Message>
                )}
              </div>
            )}

            {versionEditorTab === 'templates' && (
              <div className="version-templates-editor">
                <TemplatesEditor
                  templates={versionForm.templates}
                  onChange={(templates) => setVersionForm({ ...versionForm, templates })}
                />
              </div>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowNewVersionModal(false)}>取消</Button>
          <Button primary onClick={handleCreateVersion} disabled={syntaxErrors.length > 0}>
            <Icon name="check" /> 创建版本
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 编辑配置模态框 */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} size="small">
        <Modal.Header>
          <Icon name="cog" /> 编辑配置 - {selectedApi?.name}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>显示名称</label>
              <Input
                placeholder="友好的显示名称"
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>描述</label>
              <TextArea
                placeholder="API功能描述"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
              />
            </Form.Field>
            <Form.Field>
              <label>分类</label>
              <Input
                placeholder="如：工具、助手、翻译"
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Field>
                <label>执行超时（秒）</label>
                <Input
                  type="number"
                  min={1}
                  max={300}
                  value={editForm.timeout}
                  onChange={(e) => setEditForm({ ...editForm, timeout: parseInt(e.target.value) || 30 })}
                />
                <small style={{ color: '#888' }}>JS代码执行的最大时间，1-300秒</small>
              </Form.Field>
              <Form.Field>
                <label>LLM调用限制（次）</label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={editForm.rate_limit}
                  onChange={(e) => setEditForm({ ...editForm, rate_limit: parseInt(e.target.value) || 10 })}
                />
                <small style={{ color: '#888' }}>单次执行中llm.chat()的最大调用次数</small>
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowEditModal(false)}>取消</Button>
          <Button primary onClick={handleSaveConfig}>
            <Icon name="check" /> 保存
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default DynamicAPI;
